import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * Pengaturan tema aplikasi: "light" | "dark" | "system".
 * Pilihan disimpan di localStorage ("wms.theme") dan diterapkan sebagai class
 * "dark" pada <html>, sehingga varian `dark:` Tailwind dan CSS variable di
 * index.css ikut berubah.
 */

export const THEME_STORAGE_KEY = "wms.theme";
export const THEME_OPTIONS = ["light", "dark", "system"];

const ThemeContext = createContext(null);

const media = () =>
    typeof window !== "undefined" && window.matchMedia
        ? window.matchMedia("(prefers-color-scheme: dark)")
        : null;

const readStoredTheme = () => {
    try {
        const v = localStorage.getItem(THEME_STORAGE_KEY);
        return THEME_OPTIONS.includes(v) ? v : "light";
    } catch {
        return "light";
    }
};

const resolve = (theme, systemDark) =>
    theme === "system" ? (systemDark ? "dark" : "light") : theme;

const applyToDocument = (resolved) => {
    const root = document.documentElement;
    root.classList.toggle("dark", resolved === "dark");
    root.style.colorScheme = resolved;

    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", resolved === "dark" ? "#0B111B" : "#0A6CF0");
};

export function ThemeProvider({ children }) {
    const [theme, setThemeState] = useState(readStoredTheme);
    const [systemDark, setSystemDark] = useState(() => !!media()?.matches);

    // ikuti perubahan tema OS saat mode "system"
    useEffect(() => {
        const mq = media();
        if (!mq) return undefined;
        const onChange = (e) => setSystemDark(e.matches);
        mq.addEventListener?.("change", onChange);
        return () => mq.removeEventListener?.("change", onChange);
    }, []);

    const resolvedTheme = resolve(theme, systemDark);

    useEffect(() => {
        applyToDocument(resolvedTheme);
    }, [resolvedTheme]);

    // label & dokumen selalu dicetak dalam mode terang
    useEffect(() => {
        const before = () => applyToDocument("light");
        const after = () => applyToDocument(resolvedTheme);
        window.addEventListener("beforeprint", before);
        window.addEventListener("afterprint", after);
        return () => {
            window.removeEventListener("beforeprint", before);
            window.removeEventListener("afterprint", after);
        };
    }, [resolvedTheme]);

    // sinkron antar tab
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === THEME_STORAGE_KEY && THEME_OPTIONS.includes(e.newValue)) {
                setThemeState(e.newValue);
            }
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, []);

    const setTheme = useCallback((next) => {
        if (!THEME_OPTIONS.includes(next)) return;
        setThemeState(next);
        try {
            localStorage.setItem(THEME_STORAGE_KEY, next);
        } catch {
            /* abaikan */
        }
    }, []);

    const toggleTheme = useCallback(() => {
        setTheme(resolvedTheme === "dark" ? "light" : "dark");
    }, [resolvedTheme, setTheme]);

    const value = useMemo(
        () => ({
            theme,
            resolvedTheme,
            isDark: resolvedTheme === "dark",
            setTheme,
            toggleTheme
        }),
        [theme, resolvedTheme, setTheme, toggleTheme]
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error("useTheme harus dipakai di dalam <ThemeProvider>");
    return ctx;
}

/** Warna untuk chart (recharts tidak membaca class Tailwind). */
export const CHART_COLORS = {
    light: {
        ink: "#0A6CF0",
        inkSoft: "#7DB8FF",
        capacity: "#D3DEEC",
        line: "#DCE4EE",
        track: "#E8EEF7",
        grid: "#E8EEF7",
        tick: "#5F6B7D",
        cursor: "#F2F5FA",
        card: "#FFFFFF",
        text: "#1C2433",
        tag: "#F5A623"
    },
    dark: {
        ink: "#3D8BFF",
        inkSoft: "#7DB8FF",
        capacity: "#33425A",
        line: "#2A3546",
        track: "#232E3E",
        grid: "#232E3E",
        tick: "#94A0B3",
        cursor: "#1A2331",
        card: "#141B26",
        text: "#E6EBF2",
        tag: "#F5A623"
    }
};

export function useChartColors() {
    const { resolvedTheme } = useTheme();
    return CHART_COLORS[resolvedTheme];
}
